import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Alert, Badge, Button, Card, EmptyState, Icon, Skeleton } from '@tb/ui';
import type { BillingState, Domain } from '@tb/shared';
import { api } from '../api/endpoints';
import { useSessionUser } from '../auth/SessionProvider';
import { useWalletContext } from '../auth/WalletProvider';
import { Screen } from '../components/Layout';
import { WalletBar } from '../components/WalletBar';
import { useT, type Translate } from '../i18n/LocaleProvider';
import { useRuntimeConfig } from '../RuntimeConfigProvider';
import { openExternal } from '../telegram/telegram';

export function HomeScreen() {
  const user = useSessionUser();
  const wallet = useWalletContext();
  const config = useRuntimeConfig();
  const t = useT();

  const address = wallet.status === 'ready' ? wallet.wallet.address : null;

  const domainsQuery = useQuery({
    queryKey: ['domains', address],
    queryFn: () => api.domains.list(address!),
    enabled: address !== null,
  });

  const sitesQuery = useQuery({
    queryKey: ['sites'],
    queryFn: () => api.sites.list(),
    enabled: address !== null,
  });

  const billingQuery = useQuery({ queryKey: ['billing'], queryFn: () => api.billing.me() });

  return (
    <Screen action={<WalletBar />}>
      <div className="tb-screen">
        <div className="tb-stack" style={{ gap: 4 }}>
          <span className="tb-title">{t('home.greeting', { name: user.firstName })}</span>
          <span className="tb-subtitle">{t('home.tagline')}</span>
        </div>

        {config.network === 'testnet' ? (
          <Alert tone="warning" title={t('home.testnet.title')}>
            {t('home.testnet.body')}
          </Alert>
        ) : null}

        {billingQuery.data?.enabled ? <BillingBanner state={billingQuery.data} t={t} /> : null}

        {wallet.status === 'error' ? (
          <Alert tone="danger" title={t('wallet.notConfirmed')}>
            {wallet.message}
          </Alert>
        ) : null}

        {address === null ? (
          <ConnectPrompt
            t={t}
            onConnect={() => void wallet.connect()}
            busy={wallet.status !== 'disconnected'}
          />
        ) : domainsQuery.isPending ? (
          <LoadingList />
        ) : domainsQuery.isError ? (
          <Alert tone="danger" title={t('home.domains.error')}>
            {(domainsQuery.error as Error).message}
          </Alert>
        ) : domainsQuery.data.empty ? (
          <NoDomains t={t} marketUrl={domainsQuery.data.marketUrl} />
        ) : (
          <DomainList
            t={t}
            domains={domainsQuery.data.domains}
            onRefresh={() => void domainsQuery.refetch()}
            refreshing={domainsQuery.isFetching}
          />
        )}

        {sitesQuery.data && sitesQuery.data.length > 0 ? (
          <section className="tb-stack">
            <span className="tb-label">{t('home.recent')}</span>
            {sitesQuery.data.slice(0, 3).map((site) => (
              <SiteRow
                key={site.id}
                t={t}
                siteId={site.id}
                domain={site.domain}
                live={Boolean(site.liveVersion)}
              />
            ))}
          </section>
        ) : null}
      </div>
    </Screen>
  );
}

function BillingBanner({ state, t }: { state: BillingState; t: Translate }) {
  const navigate = useNavigate();

  if (state.trialDaysLeft !== null) {
    const lastDay = state.trialDaysLeft <= 1;
    return (
      <Alert tone={lastDay ? 'warning' : 'info'} icon="clock" title={t('billing.trial.title')}>
        <div className="tb-stack" style={{ gap: 10 }}>
          <span>
            {lastDay
              ? t('billing.trial.lastDay')
              : t('billing.trial.body', { days: state.trialDaysLeft })}
          </span>
          <Button icon="sparkles" size="sm" onClick={() => void navigate('/plans')}>
            {t('billing.choosePlan')}
          </Button>
        </div>
      </Alert>
    );
  }

  if (state.accountQuota > 0) {
    return (
      <div className="tb-row">
        <Icon name="card" size={16} />
        <span className="tb-hint tb-grow">
          {t('billing.quota', { used: state.accountQuotaUsed, quota: state.accountQuota })}
        </span>
      </div>
    );
  }

  return null;
}

function ConnectPrompt({
  t,
  onConnect,
  busy,
}: {
  t: Translate;
  onConnect: () => void;
  busy: boolean;
}) {
  return (
    <Card>
      <div className="tb-row">
        <Icon name="wallet" size={22} />
        <div className="tb-grow tb-stack" style={{ gap: 2 }}>
          <span style={{ fontWeight: 600 }}>{t('home.connect.title')}</span>
          <span className="tb-hint">{t('home.connect.body')}</span>
        </div>
      </div>
      <Button icon="connect" block onClick={onConnect} loading={busy}>
        {t('home.connect.button')}
      </Button>
    </Card>
  );
}

function NoDomains({ t, marketUrl }: { t: Translate; marketUrl: string }) {
  return (
    <EmptyState
      icon="domain"
      title={t('home.empty.title')}
      description={t('home.empty.body')}
      action={
        <div className="tb-stack" style={{ width: '100%', maxWidth: 320 }}>
          <Button icon="open-external" block onClick={() => openExternal(marketUrl)}>
            {t('home.empty.get')}
          </Button>
          <Button variant="ghost" icon="refresh" block onClick={() => window.location.reload()}>
            {t('home.empty.already')}
          </Button>
        </div>
      }
    />
  );
}

function DomainList({
  t,
  domains,
  onRefresh,
  refreshing,
}: {
  t: Translate;
  domains: Domain[];
  onRefresh: () => void;
  refreshing: boolean;
}) {
  const navigate = useNavigate();

  return (
    <section className="tb-stack">
      <div className="tb-row tb-row--between">
        <span className="tb-label">{t('home.domains.title')}</span>
        <Button size="sm" variant="ghost" icon="refresh" loading={refreshing} onClick={onRefresh}>
          {t('home.domains.refresh')}
        </Button>
      </div>

      {domains.map((domain) => (
        <Card key={domain.name}>
          <div className="domain-row">
            <Icon name="domain" size={22} />
            <div className="tb-grow tb-stack" style={{ gap: 2, minWidth: 0 }}>
              <span className="domain-row__name tb-truncate">{domain.name}</span>
              <span className="tb-hint">
                {domain.currentBagId
                  ? domain.managedByUs
                    ? t('home.domain.publishedByUs')
                    : t('home.domain.publishedElsewhere')
                  : t('home.domain.notPublished')}
              </span>
            </div>
          </div>

          <Button
            block
            variant={domain.siteId ? 'secondary' : 'primary'}
            icon={domain.siteId ? 'edit' : 'plus'}
            onClick={() =>
              void navigate(
                domain.siteId
                  ? `/sites/${domain.siteId}`
                  : `/new/${encodeURIComponent(domain.name)}`,
              )
            }
          >
            {domain.siteId ? t('home.domain.open') : t('home.domain.build')}
          </Button>
        </Card>
      ))}
    </section>
  );
}

function SiteRow({
  t,
  siteId,
  domain,
  live,
}: {
  t: Translate;
  siteId: string;
  domain: string;
  live: boolean;
}) {
  const navigate = useNavigate();
  return (
    <Card className="tb-card--interactive" onClick={() => void navigate(`/sites/${siteId}`)}>
      <div className="tb-row">
        <Icon name="globe" size={18} />
        <span className="tb-grow tb-truncate">{domain}</span>
        {live ? <Badge tone="live">{t('status.LIVE')}</Badge> : <Badge>{t('status.DRAFT')}</Badge>}
        <Icon name="chevron-right" size={16} />
      </div>
    </Card>
  );
}

function LoadingList() {
  return (
    <div className="tb-stack">
      <Skeleton height={92} />
      <Skeleton height={92} />
    </div>
  );
}
